import React, { useState } from 'react';
import { folderService } from '@/services/folderService';
import { Folder } from '@/types';
import toast from 'react-hot-toast';
import PrimaryButton from '@/Components/PrimaryButton';
import SecondaryButton from '@/Components/SecondaryButton';
import Modal from '@/Components/Modal';
import TextInput from '@/Components/TextInput';
import InputLabel from '@/Components/InputLabel';

interface FolderCreateModalProps {
    show: boolean;
    onClose: () => void;
    onFolderCreated: (folder: Folder) => void;
}

export default function FolderCreateModal({ show, onClose, onFolderCreated }: FolderCreateModalProps) {
    const [folderName, setFolderName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!folderName.trim()) {
            toast.error('Folder name is required');
            return;
        }

        setIsSubmitting(true);

        try {
            const folder = await folderService.createFolder({ name: folderName.trim() });
            toast.success('Folder created successfully');
            onFolderCreated(folder);
            handleClose();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to create folder';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFolderName('');
        onClose();
    };

    return (
        <Modal show={show} onClose={handleClose} maxWidth="md">
            <div className="p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">
                    Create New Folder
                </h3>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <InputLabel htmlFor="folderName" value="Folder Name" className="mb-2" />
                        <TextInput
                            id="folderName"
                            type="text"
                            value={folderName}
                            onChange={(e) => setFolderName(e.target.value)}
                            placeholder="Enter folder name"
                            maxLength={255}
                            disabled={isSubmitting}
                            className="w-full"
                            isFocused
                        />
                    </div>

                    <div className="flex justify-end ">
                        <SecondaryButton
                            type="button"
                            className="me-2"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton
                            type="submit"
                            disabled={isSubmitting || !folderName.trim()}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Folder'}
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </Modal>
    );
}
